import { UserFacingError } from '../utils/UserFacingError.ts'
import { loadPosCatalog } from '../features/catalog/data/load-pos-catalog.ts'
import { normalizeCatalogSnapshot } from '../features/catalog/services/catalogSnapshots.ts'
import { hasLocalSupabaseSession, supabase } from '../lib/supabase'
import { isInvalidAuthError } from '../features/session/services/sessionValidity'
export { summarizeSales } from '../features/cash-registers/services/cashSummary.ts'
export { buildSalePayload } from '../features/quick-sale/services/salePayload.ts'
import type {
  CashClosedPayload,
  CustomerFiscalSnapshot,
  HistoricalPaymentMethod,
  LoginInput,
  OfflineEvent,
  ProductSalesStat,
  SaleCreatedPayload,
  SaleRecord,
  SessionTicketRecord,
  TenantContext,
  TicketLineFiscalSnapshot,
  TicketLineModifier,
} from '../types'
import type {
  DeviceAssignmentRow,
  DeviceRow,
  MembershipRow,
  SaleRow,
  TenantRow,
  TicketLineProductSalesRow,
  UserMembershipRow,
  VenueRow,
} from '../types/supabase'
import { normalizeTenantFeatures } from '../features/platform/tenantFeatureAccess'
import {
  SESSION_TICKETS_PAGE_SIZE,
  type SessionTicketHistoryPage,
} from '../features/cash-registers/services/sessionTicketHistoryModel.ts'
import { claimLoginLease, releaseLocalLoginLock, releaseLoginLease } from './loginLeaseService'
async function requireExclusiveLogin(context: TenantContext) {
  if (await claimLoginLease()) {
    return context
  }

  releaseLocalLoginLock()
  throw new LoginLeaseConflictError('Esta cuenta ya está abierta en otro dispositivo o pestaña.', context)
}

function mapFiscalSnapshot(row: {
  line_total_cents: number
  tax_amount_cents: number | null
  tax_rate: number | null
  taxable_base_cents: number | null
}): TicketLineFiscalSnapshot | null {
  if (row.tax_rate === null || row.taxable_base_cents === null || row.tax_amount_cents === null) {
    return null
  }

  return {
    taxRate: Number(row.tax_rate),
    taxableBaseCents: row.taxable_base_cents,
    taxAmountCents: row.tax_amount_cents,
    grossTotalCents: row.line_total_cents,
  }
}

export async function loadTenantFeatures(tenantId: string, venueId?: string): Promise<ReturnType<typeof normalizeTenantFeatures> | undefined> {
  if (!supabase) return undefined
  const { data, error } = venueId
    ? await supabase.rpc('get_current_billing_venue_features', { p_tenant_id: tenantId, p_venue_id: venueId })
    : await supabase.rpc('get_current_tenant_features', { p_tenant_id: tenantId })
  if (error) {
    // Keep deployments compatible while the feature migration and PostgREST schema cache propagate.
    if (['42883', 'PGRST202'].includes(error.code ?? '')) return venueId ? loadTenantFeatures(tenantId) : undefined
    throw error
  }
  return normalizeTenantFeatures(data)
}

export async function loadBillingActivationRequired(tenantId: string): Promise<boolean> {
  if (!supabase) throw new Error('Supabase no está configurado.')
  const { data, error } = await supabase.rpc('get_current_billing_access', { p_tenant_id: tenantId })
  if (error) {
    // Existing backends cannot create payment-gated tenants before the expand.
    if (['42883', 'PGRST202'].includes(error.code ?? '')) return false
    throw error
  }
  return data === true
}

export async function loginTenant(input: LoginInput): Promise<TenantContext> {
  if (!supabase) {
    throw new Error('Supabase no está configurado.')
  }

  const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  })

  if (authError) {
    throw authError
  }

  const user = authData.user

  if (!user) {
    throw new Error('No se ha recibido usuario autenticado.')
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('full_name, is_superadmin')
    .eq('id', user.id)
    .maybeSingle<{ full_name: string | null; is_superadmin: boolean }>()

  if (profileError) {
    throw profileError
  }

  if (profile?.is_superadmin) {
    return requireExclusiveLogin({
      tenantId: '',
      tenantName: 'Plataforma TICKIT',
      tenantSlug: '',
      venueId: '',
      venueName: '',
      deviceId: '',
      deviceName: '',
      userId: user.id,
      userName: profile.full_name ?? user.user_metadata.full_name ?? user.email ?? 'Superadmin',
      role: 'superadmin',
    })
  }

  const { data: memberships, error: membershipError } = await supabase
    .from('tenant_memberships')
    .select('tenant_id, role')
    .eq('user_id', user.id)
    .eq('is_active', true)
    .limit(2)

  if (membershipError) {
    throw membershipError
  }

  const activeMemberships = (memberships ?? []) as UserMembershipRow[]

  if (activeMemberships.length === 0) {
    throw new Error(`El usuario ${user.email ?? user.id} no tiene acceso activo a ningún negocio.`)
  }

  if (activeMemberships.length > 1) {
    throw new UserFacingError('Este usuario pertenece a más de un negocio. Usa una cuenta diferente para cada negocio.')
  }

  const membership = activeMemberships[0]
  const { data: tenant, error: tenantError } = await supabase
    .from('tenants')
    .select('id, name, slug, is_active')
    .eq('id', membership.tenant_id)
    .single<TenantRow>()

  if (tenantError || !tenant || tenant.is_active === false) {
    throw tenantError
  }

  const features = membership.role === 'owner' || membership.role === 'manager' ? await loadTenantFeatures(tenant.id) : undefined

  if (membership.role === 'owner' || membership.role === 'manager') {
    return requireExclusiveLogin({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSlug: tenant.slug,
      venueId: '',
      venueName: '',
      deviceId: '',
      deviceName: '',
      userId: user.id,
      userName: user.user_metadata.full_name ?? user.email ?? 'Usuario CRM',
      role: membership.role,
      billingActivationRequired: await loadBillingActivationRequired(tenant.id),
      features,
    })
  }

  if (membership.role !== 'cashier') {
    throw new UserFacingError('Este usuario no tiene acceso al CRM ni una cuenta de caja compatible.')
  }

  const { data: assignment, error: assignmentError } = await supabase
    .from('device_user_assignments')
    .select('tenant_id, user_id, venue_id, device_id, is_active')
    .eq('tenant_id', tenant.id)
    .eq('user_id', user.id)
    .eq('is_active', true)
    .maybeSingle<DeviceAssignmentRow>()

  if (assignmentError) {
    throw assignmentError
  }

  if (!assignment) {
    throw new UserFacingError('Este usuario no tiene ningún dispositivo activo asignado. Contacta con el propietario.')
  }

  const [{ data: venue, error: venueError }, { data: device, error: deviceError }] = await Promise.all([
    supabase
      .from('venues')
      .select('id, name, address, legal_name, tax_id, timezone, default_tax_rate')
      .eq('tenant_id', tenant.id)
      .eq('id', assignment.venue_id)
      .eq('is_active', true)
      .maybeSingle<VenueRow>(),
    supabase
      .from('devices')
      .select('id, name, device_mode, default_cash_register_id, can_take_orders, can_take_payments, can_open_cash_session, can_close_cash_session, can_manage_cash, production_destination_id')
      .eq('tenant_id', tenant.id)
      .eq('venue_id', assignment.venue_id)
      .eq('id', assignment.device_id)
      .eq('is_active', true)
      .maybeSingle<DeviceRow>(),
  ])

  if (venueError) {
    throw venueError
  }

  if (deviceError) {
    throw deviceError
  }

  if (!venue || !device) {
    throw new Error('El local o dispositivo asignado está desactivado o ya no existe.')
  }

  return requireExclusiveLogin({
    tenantId: tenant.id,
    tenantName: tenant.name,
    tenantSlug: tenant.slug,
    venueId: venue.id,
    venueName: venue.name,
    venueAddress: venue.address ?? undefined,
    venueLegalName: venue.legal_name ?? undefined,
    venueTaxId: venue.tax_id ?? undefined,
    venueTimeZone: venue.timezone,
    venueDefaultTaxRate: Number(venue.default_tax_rate),
    deviceId: device.id,
    deviceName: device.name,
    deviceMode: device.device_mode,
    productionDestinationId: device.production_destination_id,
    defaultCashRegisterId: device.default_cash_register_id,
    canTakeOrders: device.can_take_orders,
    canTakePayments: device.can_take_payments,
    canOpenCashSession: device.can_open_cash_session,
    canCloseCashSession: device.can_close_cash_session,
    canManageCash: device.can_manage_cash,
    userId: user.id,
    userName: user.user_metadata.full_name ?? user.email ?? 'Usuario',
    role: membership.role,
    features: await loadTenantFeatures(tenant.id, venue.id),
  })
}

export class TenantSessionError extends UserFacingError {
  constructor(message: string) {
    super(message)
    this.name = 'TenantSessionError'
  }
}

export class LoginLeaseConflictError extends TenantSessionError {
  readonly context: TenantContext

  constructor(message: string, context: TenantContext) {
    super(message)
    this.name = 'LoginLeaseConflictError'
    this.context = context
  }
}

export async function restoreTenantContext(cachedContext: TenantContext): Promise<TenantContext> {
  if (!supabase) {
    throw new Error('Supabase no está configurado.')
  }

  // Refresh first: an expired access token is not evidence of a revoked session.
  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession()
  if (refreshError) {
    if (isInvalidAuthError(refreshError)) throw new TenantSessionError('La sesión ha caducado. Inicia sesión de nuevo.')
    throw refreshError
  }
  if (!refreshed.session || refreshed.session.user.id !== cachedContext.userId) {
    throw new TenantSessionError('La sesión guardada no pertenece al usuario de este TPV.')
  }
  const { data: authData, error: authError } = await supabase.auth.getUser()

  if (authError) {
    if (isInvalidAuthError(authError)) {
      throw new TenantSessionError('La sesión ha caducado. Inicia sesión de nuevo.')
    }

    throw authError
  }

  const user = authData.user

  if (!user || user.id !== cachedContext.userId) {
    throw new TenantSessionError('La sesión guardada no pertenece al usuario de este TPV.')
  }

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('full_name, is_superadmin')
    .eq('id', user.id)
    .maybeSingle<{ full_name: string | null; is_superadmin: boolean }>()

  if (profileError) {
    throw profileError
  }

  if (profile?.is_superadmin) {
    return requireExclusiveLogin({
      tenantId: '',
      tenantName: 'Plataforma TICKIT',
      tenantSlug: '',
      venueId: '',
      venueName: '',
      deviceId: '',
      deviceName: '',
      userId: user.id,
      userName: profile.full_name ?? user.user_metadata.full_name ?? user.email ?? 'Superadmin',
      role: 'superadmin',
    })
  }

  const [{ data: tenant, error: tenantError }, { data: membership, error: membershipError }] = await Promise.all([
    supabase
      .from('tenants')
      .select('id, name, slug, is_active')
      .eq('id', cachedContext.tenantId)
      .maybeSingle<TenantRow>(),
    supabase
      .from('tenant_memberships')
      .select('role')
      .eq('tenant_id', cachedContext.tenantId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle<MembershipRow>(),
  ])

  if (tenantError || membershipError) {
    throw tenantError ?? membershipError
  }

  if (!tenant || tenant.is_active === false || !membership) {
    throw new TenantSessionError('El usuario ya no tiene acceso activo a este negocio.')
  }

  const features = membership.role === 'owner' || membership.role === 'manager' ? await loadTenantFeatures(tenant.id) : undefined

  if (membership.role === 'owner' || membership.role === 'manager') {
    return requireExclusiveLogin({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSlug: tenant.slug,
      venueId: '',
      venueName: '',
      deviceId: '',
      deviceName: '',
      userId: user.id,
      userName: user.user_metadata.full_name ?? user.email ?? 'Usuario CRM',
      role: membership.role,
      billingActivationRequired: await loadBillingActivationRequired(tenant.id),
      features,
    })
  }

  const { data: assignment, error: assignmentError } = await supabase
    .from('device_user_assignments')
    .select('tenant_id, user_id, venue_id, device_id, is_active')
    .eq('tenant_id', tenant.id)
    .eq('user_id', user.id)
    .eq('is_active', true)
    .maybeSingle<DeviceAssignmentRow>()

  if (assignmentError) {
    throw assignmentError
  }

  if (membership.role !== 'cashier' || !assignment) {
    throw new TenantSessionError('El usuario ya no tiene un dispositivo activo asignado.')
  }

  const [{ data: venue, error: venueError }, { data: device, error: deviceError }] = await Promise.all([
    supabase
      .from('venues')
      .select('id, name, address, legal_name, tax_id, timezone, default_tax_rate')
      .eq('tenant_id', tenant.id)
      .eq('id', assignment.venue_id)
      .eq('is_active', true)
      .maybeSingle<VenueRow>(),
    supabase
      .from('devices')
      .select('id, name, device_mode, default_cash_register_id, can_take_orders, can_take_payments, can_open_cash_session, can_close_cash_session, can_manage_cash, production_destination_id')
      .eq('tenant_id', tenant.id)
      .eq('venue_id', assignment.venue_id)
      .eq('id', assignment.device_id)
      .eq('is_active', true)
      .maybeSingle<DeviceRow>(),
  ])

  if (venueError || deviceError) {
    throw venueError ?? deviceError
  }

  if (!venue || !device) {
    throw new TenantSessionError('El local o dispositivo asignado está desactivado.')
  }

  return requireExclusiveLogin({
    tenantId: tenant.id,
    tenantName: tenant.name,
    tenantSlug: tenant.slug,
    venueId: venue.id,
    venueName: venue.name,
    venueAddress: venue.address ?? undefined,
    venueLegalName: venue.legal_name ?? undefined,
    venueTaxId: venue.tax_id ?? undefined,
    venueTimeZone: venue.timezone,
    venueDefaultTaxRate: Number(venue.default_tax_rate),
    deviceId: device.id,
    deviceName: device.name,
    deviceMode: device.device_mode,
    productionDestinationId: device.production_destination_id,
    defaultCashRegisterId: device.default_cash_register_id,
    canTakeOrders: device.can_take_orders,
    canTakePayments: device.can_take_payments,
    canOpenCashSession: device.can_open_cash_session,
    canCloseCashSession: device.can_close_cash_session,
    canManageCash: device.can_manage_cash,
    userId: user.id,
    userName: user.user_metadata.full_name ?? user.email ?? 'Usuario',
    role: membership.role,
    features: await loadTenantFeatures(tenant.id, venue.id),
  })
}

export async function hasValidOfflineSession(context: TenantContext) {
  // getSession may perform network refresh. Access-token expiry alone must not
  // discard a previously authenticated POS while its refresh token is offline.
  return Boolean(supabase && hasLocalSupabaseSession(context.userId))
}

export async function logoutTenant() {
  if (!supabase) {
    return
  }

  let leaseError: unknown = null

  try {
    await releaseLoginLease()
  } catch (error) {
    leaseError = error
    releaseLocalLoginLock()
  }

  const { error } = await supabase.auth.signOut({ scope: 'local' })

  if (error || leaseError) {
    throw error ?? leaseError
  }
}

export async function loadPosCatalogFromSupabase(context: TenantContext, force = false) {
  return loadPosCatalog(context, force)
}

export async function loadOpenCashSession(context: TenantContext) {
  if (!supabase) {
    return null
  }

  const { data, error } = await supabase
    .from('cash_sessions')
    .select('id, tenant_id, venue_id, opened_by_device_id, opened_by, opened_at, opening_float_cents, cash_register_id, cash_registers(name), selected_by:devices!devices_active_cash_session_id_fkey!inner(id, is_active)')
    .eq('tenant_id', context.tenantId)
    .eq('venue_id', context.venueId)
    .eq('selected_by.id', context.deviceId)
    .eq('selected_by.is_active', true)
    .eq('status', 'open')
    .maybeSingle()

  if (error) {
    throw error
  }

  if (!data) {
    return null
  }

  const selected = data

  return {
    id: selected.id as string,
    tenantId: selected.tenant_id as string,
    venueId: selected.venue_id as string,
    deviceId: selected.opened_by_device_id as string,
    cashRegisterId: selected.cash_register_id as string,
    cashRegisterName: (selected.cash_registers as unknown as { name?: string } | null)?.name ?? 'Caja',
    userId: selected.opened_by as string,
    openedAt: selected.opened_at as string,
    openingFloatCents: selected.opening_float_cents as number,
    status: 'open' as const,
  }
}

export async function loadSalesLedgerFromSupabase(context: TenantContext, cashSessionId: string) {
  if (!supabase) {
    return []
  }

  const { data, error } = await supabase
    .from('sales')
    // Fiscal annulments retain sales/payments as immutable historical snapshots.
    .select('id, cash_session_id, payment_method, total_cents, created_at, sale_payments(method, amount_cents), tickets!inner(status, venue_id)')
    .eq('tenant_id', context.tenantId)
    .eq('cash_session_id', cashSessionId)
    .eq('tickets.status', 'paid')
    .eq('tickets.venue_id', context.venueId)
    .order('created_at', { ascending: true })

  if (error) {
    throw error
  }

  return ((data ?? []) as Array<SaleRow & { sale_payments?: Array<{ method: HistoricalPaymentMethod; amount_cents: number }> }>).map((sale) => ({
    id: sale.id,
    cashSessionId: sale.cash_session_id,
    paymentMethod: sale.payment_method,
    totalCents: sale.total_cents,
    createdAt: sale.created_at,
    payments: sale.sale_payments?.map(payment => ({ method: payment.method, amountCents: payment.amount_cents })),
  }))
}

type SessionTicketQueryRow = {
  id: string
  tenant_id: string
  cash_session_id: string
  cash_register_id: string
  venue_id: string
  device_id: string
  user_id: string
   status: 'paid' | 'void'
   refund_requests: Array<{
     id: string
     original_ticket_id: string
     total_cents: number
     refund_method: HistoricalPaymentMethod
     created_at: string
     fiscal_rectificative_record: unknown
     refund_lines: Array<{
       id: string
       original_ticket_line_id: string
       quantity: number
       gross_cents: number
       discount_cents: number
       net_total_cents: number
       product_name: string
       variant_name: string
       modifiers: TicketLineModifier[] | null
       tax_rate: number | null
       taxable_base_cents: number | null
       tax_amount_cents: number | null
     }> | null
     refund_payments: Array<{ id: string; method: HistoricalPaymentMethod; amount_cents: number; payment_snapshot: { receivedCents?: number; changeCents?: number } | null }> | null
     fiscal_local_records: Array<{
       id: string
       invoice_id: string
       invoice_snapshot: { issuerName: string; issuerNif: string; issuerAddress?: string; series: string; number: number; issuedAt: string; qrUrl: string; transmissionMode?: 'bridge' | 'local-only'; recipient: { name: string; nif: string } | null }
       record_envelope: unknown
     }> | null
   }> | null

   subtotal_cents: number

  discount_id: string | null
  discount_name: string | null
  discount_type: 'percentage' | 'fixed' | 'manual' | null
  discount_value_type: 'percentage' | 'fixed' | null
  discount_value: number | string | null
  discount_rounding_increment_cents: 5 | 10 | 50 | 100 | null
  discount_amount_cents: number | null
  discount_rule_kind: 'discount' | 'promotion' | null
  discount_scope: 'general' | 'specific' | null
  discount_automatic: boolean
  discount_snapshot: {
    fixedApplication?: 'ticket' | 'unit' | 'line'
    targets?: Array<{ productId: string; variantId: string | null }>
    activeWeekdays?: number[]
    startsAt?: string | null
    endsAt?: string | null
  } | null
  total_cents: number
  local_created_at: string
  is_invoice: boolean
  customer_id: string | null
  customer_snapshot: CustomerFiscalSnapshot | null
  invoice_series: string | null
  invoice_number: string | null
   invoice_issued_at: string | null
   ticket_number: number | string
   fiscal_local_records: Array<{
     id: string
     invoice_id: string
     record_kind: 'alta' | 'anulacion'
     invoice_snapshot: {
       issuerName: string
       issuerNif: string
       issuerAddress?: string
       series: string
       number: number
       issuedAt: string
       qrUrl: string
       transmissionMode?: 'bridge' | 'local-only'
       recipient: { name: string; nif: string } | null
     }
   }> | null
   ticket_lines: Array<{
    id: string
    product_id: string | null
    variant_id: string | null
    product_name: string
    variant_name: string
    quantity: number
    allocated_quantity: number | null
    unit_price_cents: number
    line_total_cents: number
    tax_rate: number | null
    taxable_base_cents: number | null
    tax_amount_cents: number | null
    discount_amount_cents: number
    net_total_cents: number
    modifiers: TicketLineModifier[]
    sale_format_id: string | null
    sale_format_name_snapshot: string | null
    category_id_snapshot: string | null
    category_name_snapshot: string | null
    catalog_tab_id_snapshot: string | null
    catalog_tab_name_snapshot: string | null
    base_price_cents: number | null
    component_delta_cents: number | null
    modifier_delta_cents: number | null
    gross_before_discount_cents: number | null
    ticket_line_components: Array<{
      id: string; component_type: 'mixer' | 'menu_component'; selection_group_id: string | null; selection_group_name_snapshot: string
      product_id: string | null; variant_id: string | null; product_name_snapshot: string; variant_name_snapshot: string
      quantity: number; price_delta_cents: number; sort_order: number
      metadata: { modifiers?: Array<{ id: string; groupId: string; name: string; priceCents: number }> } | null
    }> | null
  }> | null
  sales: Array<{
    id: string
    payment_method: HistoricalPaymentMethod | null
    total_cents: number
    local_created_at: string
    sale_payments: Array<{
      id: string
      method: HistoricalPaymentMethod
      amount_cents: number
      received_cents: number | null
      change_cents: number
      cashlogy_request_id: string | null
      cashlogy_transaction_id: string | null
      created_at?: string
      equal_split_part_number?: number | null
    }> | null
  }> | null
}

async function loadLoggedSalePayloads(context: TenantContext, cashSessionId: string, ticketIds: string[]) {
  if (!supabase || !ticketIds.length) return []
  const rows: Array<{ payload: unknown }> = []
  let legacy = false
  for (let offset = 0; ; offset += 500) {
    let query = supabase.from('offline_event_log').select('payload')
      .eq('tenant_id', context.tenantId).eq('event_kind', 'sale_created')
    query = legacy
      ? query.filter('payload->ticket->>cashSessionId', 'eq', cashSessionId)
      : query.eq('sale_cash_session_id', cashSessionId)
    if (ticketIds.length <= 100) query = query.in(legacy ? 'payload->ticket->>id' : 'sale_ticket_id', ticketIds)
    const { data, error } = await query.order('id').range(offset, offset + 499)
    // N-1 compatibility when the expanded schema has not reached this deployment yet.
    if (error && !legacy && ['42703', 'PGRST204'].includes(error.code)) {
      legacy = true
      offset = -500
      continue
    }
    if (error) throw error
    rows.push(...(data ?? []))
    if ((data?.length ?? 0) < 500) return rows
  }
}

async function loadSessionTicketRecordsFromSupabase(
  context: TenantContext,
  cashSessionId: string,
  ticketIds?: string[],
): Promise<SessionTicketRecord[]> {
  if (!supabase) {
    throw new Error('Supabase no está configurado.')
  }

  if (ticketIds && !ticketIds.length) return []

  let ticketQuery = supabase
    .from('tickets')
    .select(`
        id,
        tenant_id,
        cash_session_id,
        cash_register_id,
        venue_id,
        device_id,
        user_id,
        status,
        subtotal_cents,
        discount_id,
        discount_name,
        discount_type,
        discount_value_type,
        discount_value,
        discount_rounding_increment_cents,
        discount_amount_cents,
         total_cents,
         ticket_number,
         local_created_at,
         is_invoice,
         customer_id,
         customer_snapshot,
          refund_requests (
            id, original_ticket_id, total_cents, refund_method, created_at, fiscal_rectificative_record,
            refund_lines (id, original_ticket_line_id, quantity, gross_cents, discount_cents, net_total_cents, product_name, variant_name, modifiers, tax_rate, taxable_base_cents, tax_amount_cents),
            refund_payments (id, method, amount_cents, payment_snapshot),
            fiscal_local_records (id, invoice_id, invoice_snapshot, record_envelope)
          ),


        invoice_series,
        invoice_number,
         invoice_issued_at,
         fiscal_local_records (
     id,
     invoice_id,
     record_kind,
     invoice_snapshot
         ),
         discount_rule_kind,
        discount_scope,
        discount_automatic,
        discount_snapshot,
        ticket_lines (
          id,
          product_id,
          variant_id,
          product_name,
          variant_name,
          quantity,
          allocated_quantity,
          unit_price_cents,
          line_total_cents,
          tax_rate,
          taxable_base_cents,
          tax_amount_cents,
          discount_amount_cents,
          net_total_cents,
          modifiers
          ,sale_format_id,
          sale_format_name_snapshot,
          category_id_snapshot,
          category_name_snapshot,
          catalog_tab_id_snapshot,
          catalog_tab_name_snapshot,
          base_price_cents,
          component_delta_cents,
          modifier_delta_cents,
          gross_before_discount_cents,
          ticket_line_components (
            id, component_type, selection_group_id, selection_group_name_snapshot,
            product_id, variant_id, product_name_snapshot, variant_name_snapshot,
            quantity, price_delta_cents, sort_order, metadata
          )
        ),
        sales (
          id,
          payment_method,
          total_cents,
          local_created_at,
          sale_payments (
            id,
            method,
            amount_cents,
            received_cents,
            change_cents,
            cashlogy_request_id,
            cashlogy_transaction_id,
            created_at,
            equal_split_part_number
          )
        )
    `)
    .eq('tenant_id', context.tenantId)
    .eq('cash_session_id', cashSessionId)
  if (ticketIds) {
    ticketQuery = ticketQuery.in('id', ticketIds)
  }

  const { data: ticketData, error: ticketsError } = await ticketQuery.order('local_created_at', { ascending: false })
  if (ticketsError) throw ticketsError
  const eventData = await loadLoggedSalePayloads(context, cashSessionId, (ticketData ?? []).map(ticket => ticket.id))

  const loggedPayloads = new Map<string, SaleCreatedPayload>()

  for (const row of eventData ?? []) {
    const payload = row.payload as SaleCreatedPayload | null

    if (payload?.ticket?.id) {
      loggedPayloads.set(payload.ticket.id, payload)
    }
  }

  return ((ticketData ?? []) as SessionTicketQueryRow[]).map((ticket) => {
    const loggedPayload = loggedPayloads.get(ticket.id)
    const sale = ticket.sales?.[0]
    const paymentRows = [...(sale?.sale_payments ?? [])].sort((left, right) => (left.created_at ?? '').localeCompare(right.created_at ?? '') || left.id.localeCompare(right.id))
    const payment = paymentRows.length === 1 ? paymentRows[0] : undefined
    const saleId = sale?.id ?? loggedPayload?.sale.id ?? ticket.id
    const paymentMethod = sale?.payment_method ?? loggedPayload?.sale.paymentMethod ?? null
    const createdAt = sale?.local_created_at ?? loggedPayload?.sale.createdAt ?? ticket.local_created_at
    const lines = (ticket.ticket_lines ?? []).map((line) => {
      const loggedLine = loggedPayload?.lines.find((item) => item.id === line.id)

      return {
        id: line.id,
        ticketId: ticket.id,
        tenantId: ticket.tenant_id,
        productId: line.product_id ?? loggedLine?.productId ?? '',
        variantId: line.variant_id ?? loggedLine?.variantId ?? '',
        productName: line.product_name,
        variantName: line.variant_name,
        basePriceCents: loggedLine?.basePriceCents ?? line.base_price_cents ?? line.unit_price_cents - (line.modifiers ?? []).reduce((total, modifier) => total + modifier.priceCents, 0),
        componentDeltaCents: loggedLine?.componentDeltaCents ?? line.component_delta_cents ?? 0,
        modifierDeltaCents: loggedLine?.modifierDeltaCents ?? line.modifier_delta_cents ?? (line.modifiers ?? []).reduce((total, modifier) => total + modifier.priceCents, 0),
        grossBeforeDiscountCents: loggedLine?.grossBeforeDiscountCents ?? line.gross_before_discount_cents ?? line.unit_price_cents,
        quantity: Number(line.allocated_quantity ?? line.quantity),
        unitPriceCents: line.unit_price_cents,
        lineTotalCents: line.line_total_cents,
        fiscalSnapshot: mapFiscalSnapshot(line),
        discountAmountCents: line.discount_amount_cents,
        netTotalCents: line.net_total_cents,
        note: loggedLine?.note ?? null,
        modifiers: line.modifiers ?? [],
        components: loggedLine?.components ?? (line.ticket_line_components ?? []).map((component) => ({
          id: component.id, type: component.component_type, selectionGroupId: component.selection_group_id,
          selectionGroupName: component.selection_group_name_snapshot, productId: component.product_id ?? '',
          variantId: component.variant_id, productName: component.product_name_snapshot,
          variantName: component.variant_name_snapshot, quantity: component.quantity,
          priceDeltaCents: component.price_delta_cents, sortOrder: component.sort_order,
          modifiers: component.metadata?.modifiers ?? [],
        })),
        catalogSnapshot: normalizeCatalogSnapshot(loggedLine?.catalogSnapshot ?? { saleFormatId: line.sale_format_id, saleFormatName: line.sale_format_name_snapshot ?? line.variant_name, categoryId: line.category_id_snapshot, categoryName: line.category_name_snapshot ?? '', catalogTabId: line.catalog_tab_id_snapshot, catalogTabName: line.catalog_tab_name_snapshot ?? '' }, { productId: line.product_id ?? loggedLine?.productId ?? null, productName: line.product_name, variantId: line.variant_id ?? loggedLine?.variantId ?? null, variantName: line.variant_name, basePriceCents: loggedLine?.basePriceCents ?? line.base_price_cents ?? line.unit_price_cents }),
      }
    })
    const localFiscalRecord = ticket.fiscal_local_records?.find((record) => record.record_kind === 'alta')
    const localFiscalInvoice = localFiscalRecord?.invoice_snapshot
    const payload: SaleCreatedPayload = {
       ticket: {
         id: ticket.id,
         ticketNumber: Number(ticket.ticket_number),
         tenantId: ticket.tenant_id,
        cashSessionId: ticket.cash_session_id,
        cashRegisterId: ticket.cash_register_id,
        venueId: ticket.venue_id,
        deviceId: ticket.device_id,
        userId: ticket.user_id,
        subtotalCents: ticket.subtotal_cents,
        discount: ticket.discount_type && ticket.discount_name && ticket.discount_value_type && ticket.discount_value !== null
          ? {
              discountId: ticket.discount_id,
              name: ticket.discount_name,
              type: ticket.discount_type,
              calculationType: ticket.discount_value_type,
              value: ticket.discount_value_type === 'fixed'
                ? Math.round(Number(ticket.discount_value) * 100)
                : Number(ticket.discount_value),
              fixedApplication: ticket.discount_snapshot?.fixedApplication === 'unit'
                || ticket.discount_snapshot?.fixedApplication === 'line'
                ? 'unit'
                : 'ticket',
              roundingIncrementCents: ticket.discount_rounding_increment_cents,
              color: null,
              ruleKind: ticket.discount_rule_kind ?? 'discount',
              scope: ticket.discount_scope ?? 'general',
              targets: ticket.discount_snapshot?.targets ?? [],
              requiresPin: false,
              activeWeekdays: ticket.discount_snapshot?.activeWeekdays ?? [],
              startsAt: ticket.discount_snapshot?.startsAt ?? null,
              endsAt: ticket.discount_snapshot?.endsAt ?? null,
              automatic: ticket.discount_automatic,
            }
          : null,
        discountAmountCents: ticket.discount_amount_cents ?? 0,
        totalCents: ticket.total_cents,
        createdAt: ticket.local_created_at,
        invoice: ticket.is_invoice && ticket.customer_id && ticket.customer_snapshot ? {
          customerId: ticket.customer_id,
          customer: ticket.customer_snapshot,
          series: ticket.invoice_series,
          number: ticket.invoice_number,
          issuedAt: ticket.invoice_issued_at,
        } : null,
      },
      lines,
      sale: {
        id: saleId,
        tenantId: ticket.tenant_id,
        ticketId: ticket.id,
        cashSessionId: ticket.cash_session_id,
        cashRegisterId: ticket.cash_register_id,
        venueId: ticket.venue_id,
        deviceId: ticket.device_id,
        userId: ticket.user_id,
        totalCents: sale?.total_cents ?? ticket.total_cents,
        paymentMethod,
        createdAt,
      },
      payment: payment && (payment.method === 'cash' || payment.method === 'card') ? {
        id: payment.id,
        tenantId: ticket.tenant_id,
        saleId,
        method: payment.method,
        amountCents: payment.amount_cents,
        receivedCents: payment.received_cents,
        changeCents: payment.change_cents,
        cashlogyRequestId: payment.cashlogy_request_id,
        cashlogyTransactionId: payment.cashlogy_transaction_id,
        partNumber: payment.equal_split_part_number ?? undefined,
      } : null,
      payments: paymentRows.length > 1 ? paymentRows.flatMap((payment) => payment.method === 'cash' || payment.method === 'card' ? [{
        id: payment.id, tenantId: ticket.tenant_id, saleId, method: payment.method, amountCents: payment.amount_cents,
        receivedCents: payment.received_cents, changeCents: payment.change_cents,
        cashlogyRequestId: payment.cashlogy_request_id, cashlogyTransactionId: payment.cashlogy_transaction_id,
        partNumber: payment.equal_split_part_number ?? undefined,
      }] : []) : undefined,
      ...(localFiscalRecord && localFiscalInvoice ? {
        localFiscal: {
          recordId: localFiscalRecord.id,
          series: localFiscalInvoice.series,
          number: localFiscalInvoice.number,
          issuedAt: localFiscalInvoice.issuedAt,
          documentKind: localFiscalInvoice.recipient ? 'complete' as const : 'simplified' as const,
          issuerName: localFiscalInvoice.issuerName,
          issuerNif: localFiscalInvoice.issuerNif,
          issuerAddress: localFiscalInvoice.issuerAddress ?? '',
          verifactuLegend: localFiscalInvoice.transmissionMode !== 'local-only',
        },
        fiscal: {
          invoiceId: localFiscalRecord.invoice_id,
          provider: 'verifactu' as const,
          status: 'pending' as const,
          uuid: null,
          qrBase64: null,
          verificationUrl: localFiscalInvoice.transmissionMode === 'local-only' ? '' : localFiscalInvoice.qrUrl,
          externalCode: `${localFiscalInvoice.series}/${localFiscalInvoice.number}`,
          errorCode: null,
          errorMessage: null,
        },
      } : {}),
    }

     const refundDocuments = (ticket.refund_requests ?? []).map((request) => {
       const invoice = request.fiscal_local_records?.[0]?.invoice_snapshot
       if (!invoice) return null
       const refundLines = (request.refund_lines ?? []).map((refundLine) => {
         const originalLine = lines.find((line) => line.id === refundLine.original_ticket_line_id)
         if (!originalLine) return null
         return {
           ...originalLine,
           id: refundLine.id,
           ticketId: request.id,
           productName: refundLine.product_name,
           variantName: refundLine.variant_name,
           quantity: -refundLine.quantity,
           grossBeforeDiscountCents: -Math.abs(originalLine.grossBeforeDiscountCents * refundLine.quantity),
           lineTotalCents: refundLine.net_total_cents,
           discountAmountCents: refundLine.discount_cents,
           netTotalCents: refundLine.net_total_cents,
           fiscalSnapshot: mapFiscalSnapshot({ ...refundLine, line_total_cents: refundLine.net_total_cents }),
           modifiers: refundLine.modifiers ?? originalLine.modifiers,
         }
       }).filter((line): line is NonNullable<typeof line> => line !== null)
       const refundPayment = request.refund_payments?.[0]
       const refundPayload: SaleCreatedPayload = {
         ...payload,
         ticket: { ...payload.ticket, id: request.id, totalCents: request.total_cents, subtotalCents: request.total_cents, discountAmountCents: 0, discount: null, createdAt: request.created_at, invoice: null },
         lines: refundLines,
         sale: { ...payload.sale, id: request.id, ticketId: request.id, totalCents: request.total_cents, paymentMethod: request.refund_method, createdAt: request.created_at },
         payment: refundPayment && (refundPayment.method === 'cash' || refundPayment.method === 'card') ? { id: refundPayment.id, tenantId: ticket.tenant_id, saleId: request.id, method: refundPayment.method, amountCents: refundPayment.amount_cents, receivedCents: refundPayment.payment_snapshot?.receivedCents ?? null, changeCents: refundPayment.payment_snapshot?.changeCents ?? 0 } : null,
          localFiscal: { recordId: request.fiscal_local_records?.[0]?.id ?? request.id, series: invoice.series, number: invoice.number, issuedAt: invoice.issuedAt, documentKind: invoice.recipient ? 'complete' : 'simplified', issuerName: invoice.issuerName, issuerNif: invoice.issuerNif, issuerAddress: invoice.issuerAddress ?? '', verifactuLegend: invoice.transmissionMode !== 'local-only', rectifiedInvoice: { series: payload.localFiscal?.series ?? '', number: payload.localFiscal?.number ?? 0, issuedAt: payload.localFiscal?.issuedAt ?? ticket.local_created_at } },
         fiscal: { invoiceId: request.fiscal_local_records?.[0]?.invoice_id ?? request.id, provider: 'verifactu', status: 'pending', uuid: null, qrBase64: null, verificationUrl: invoice.transmissionMode === 'local-only' ? '' : invoice.qrUrl, externalCode: `${invoice.series}/${invoice.number}`, errorCode: null, errorMessage: null },
       }
       return { id: request.id, createdAt: request.created_at, payload: refundPayload }
     }).filter((document): document is NonNullable<typeof document> => document !== null)

     return {
       id: saleId,
         ticketNumber: Number(ticket.ticket_number) || undefined,
        cashSessionId: ticket.cash_session_id,
      paymentMethod,
      totalCents: ticket.total_cents,
      createdAt,
       status: ticket.status === 'void' ? 'voided' : 'active',
        isRefund: false,
        originalTicketId: null,
        refundTicketId: null,
        linkedDocumentRole: ticket.refund_requests?.length ? 'original' : null,
         linkedDocumentIds: (ticket.refund_requests ?? []).map((request) => request.id),
         refundDocuments,
        payload,

    }
  })
}

export async function loadSessionTicketsFromSupabase(
  context: TenantContext,
  cashSessionId: string,
  ticketIds?: string[],
) {
  return loadSessionTicketRecordsFromSupabase(context, cashSessionId, ticketIds)
}

const ticketLoads = new Map<string, Promise<SessionTicketRecord | null>>()

export async function loadTicketNumberFromSupabase(context: TenantContext, cashSessionId: string, ticketId: string): Promise<number | undefined> {
  if (!supabase) throw new Error('Supabase no está configurado.')
  const { data, error } = await supabase.from('tickets').select('ticket_number')
    .eq('tenant_id', context.tenantId).eq('venue_id', context.venueId)
    .eq('cash_session_id', cashSessionId).eq('id', ticketId).maybeSingle()
  if (error) throw error
  return data?.ticket_number == null ? undefined : Number(data.ticket_number)
}

export async function loadSessionTicketFromSupabase(
  context: TenantContext,
  cashSessionId: string,
  ticketId: string,
) {
  const key = `${context.tenantId}:${context.venueId}:${context.deviceId}:${context.userId}:${cashSessionId}:${ticketId}`
  const current = ticketLoads.get(key)
  if (current) return current
  const pending = loadSessionTicketRecordsFromSupabase(context, cashSessionId, [ticketId]).then(([ticket]) => ticket ?? null)
  ticketLoads.set(key, pending)
  try { return await pending } finally { if (ticketLoads.get(key) === pending) ticketLoads.delete(key) }
}

type SessionTicketPageRow = {
  ticket_id: string
  ticket_number: number | string
  total_count: number | string
}

export async function loadSessionTicketPageFromSupabase(
  context: TenantContext,
  cashSessionId: string,
  page: number,
  query: string,
): Promise<SessionTicketHistoryPage> {
  if (!supabase) throw new Error('Supabase no está configurado.')

  const requestedPage = Math.max(1, Math.trunc(page))
  const { data, error } = await supabase.rpc('pos_session_ticket_page', {
    p_tenant_id: context.tenantId,
    p_cash_session_id: cashSessionId,
    p_query: query.trim() || null,
    p_page: requestedPage,
  })
  if (error) throw error

  const rows = ((data ?? []) as SessionTicketPageRow[]).slice(0, SESSION_TICKETS_PAGE_SIZE)
  if (!rows.length && requestedPage > 1) {
    return loadSessionTicketPageFromSupabase(context, cashSessionId, 1, query)
  }

  const records = await loadSessionTicketRecordsFromSupabase(
    context,
    cashSessionId,
    rows.map((row) => row.ticket_id),
  )
  const recordsByTicketId = new Map(records.map((ticket) => [ticket.payload.ticket.id, ticket]))

  return {
    currentPage: requestedPage,
    totalResults: Number(rows[0]?.total_count ?? 0),
    tickets: rows.flatMap((row) => {
      const ticket = recordsByTicketId.get(row.ticket_id)
       return ticket ? [{ number: ticket.ticketNumber ?? 0, ticket }] : []
    }),
  }
}

export async function loadProductSalesStatsFromSupabase(context: TenantContext): Promise<ProductSalesStat[]> {
  return fetchProductSalesStats(context)
}

async function fetchProductSalesStats(context: TenantContext): Promise<ProductSalesStat[]> {
  if (!supabase) {
    return []
  }

  const aggregated: ProductSalesStat[] = []
  let afterProductId: string | null = null
  for (;;) {
    const { data, error } = await supabase.rpc('pos_product_sales_stats', {
      p_tenant_id: context.tenantId, p_venue_id: context.venueId,
      p_after_product_id: afterProductId, p_limit: 500,
    })
    if (error && ['PGRST202', '42883'].includes(error.code)) break
    if (error) throw error
    const page = (data ?? []) as Array<{ product_id: string; quantity: number | string; total_cents: number | string }>
    for (const row of page) {
      const quantity = Number(row.quantity), totalCents = Number(row.total_cents)
      if (!Number.isFinite(quantity) || !Number.isSafeInteger(totalCents)) throw new Error('Estadísticas de ventas fuera de rango.')
      aggregated.push({ productId: row.product_id, quantity, totalCents })
    }
    if (page.length < 500) return aggregated.sort((a, b) => b.quantity - a.quantity || b.totalCents - a.totalCents || a.productId.localeCompare(b.productId))
    afterProductId = page[page.length - 1].product_id
  }
  // Older backends keep a complete, paginated fallback until the expand is deployed.
  const data: TicketLineProductSalesRow[] = []
  for (let offset = 0; ; offset += 500) {
    const response = await supabase.from('ticket_lines')
      .select('product_id, quantity, allocated_quantity, line_total_cents, tickets!inner(status,venue_id)')
      .eq('tenant_id', context.tenantId).eq('tickets.status', 'paid').eq('tickets.venue_id', context.venueId)
      .not('product_id', 'is', null).order('id').range(offset, offset + 499)
    if (response.error) throw response.error
    data.push(...(response.data ?? []) as TicketLineProductSalesRow[])
    if ((response.data?.length ?? 0) < 500) break
  }

  const statsByProduct = new Map<string, ProductSalesStat>()
  const lines = (data ?? []) as TicketLineProductSalesRow[]

  lines.forEach((line) => {
    if (!line.product_id) {
      return
    }

    const current = statsByProduct.get(line.product_id) ?? {
      productId: line.product_id,
      quantity: 0,
      totalCents: 0,
    }

    statsByProduct.set(line.product_id, {
      ...current,
      quantity: current.quantity + Number(line.allocated_quantity ?? line.quantity),
      totalCents: current.totalCents + line.line_total_cents,
    })
  })

  return [...statsByProduct.values()].sort(
    (a, b) => b.quantity - a.quantity || b.totalCents - a.totalCents || a.productId.localeCompare(b.productId),
  )
}

export function mergeLedgers(localRecords: SaleRecord[], remoteRecords: SaleRecord[]) {
  const recordsById = new Map<string, SaleRecord>()

  remoteRecords.forEach((record) => recordsById.set(record.id, record))
  localRecords.forEach((record) => recordsById.set(record.id, record))

  return [...recordsById.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function syncEvent(event: OfflineEvent) {
  if (!supabase) {
    throw new Error('Supabase no está configurado.')
  }

  if (event.kind === 'cash_opened') {
    const { session } = event.payload
    const { error } = await supabase.from('cash_sessions').upsert(
      {
        id: session.id,
        tenant_id: session.tenantId,
        venue_id: session.venueId,
        device_id: session.deviceId,
        opened_by: session.userId,
        opened_at: session.openedAt,
        opening_float_cents: session.openingFloatCents,
        status: 'open',
        sync_source: 'offline_first',
      },
      { ignoreDuplicates: true, onConflict: 'id' },
    )

    if (error) {
      throw error
    }

    return
  }

  if (event.kind === 'sale_created') {
    const invoiceCustomerId = event.payload.ticket.invoice?.customerId ?? null
    const rpcName = invoiceCustomerId
      ? 'sync_invoice_sale_created'
      : 'subtotalCents' in event.payload.ticket
      ? 'sync_sale_created_v2'
      : 'sync_sale_created'
    const { error } = await supabase.rpc(rpcName, {
      p_event_id: event.id,
      p_payload: event.payload,
      ...(invoiceCustomerId ? { p_customer_id: invoiceCustomerId } : {}),
    })

    if (error) {
      throw error
    }

    return
  }

  if (event.kind === 'sale_payment_changed') {
    const { cashlogyRequestId, cashlogyTransactionId, changeCents, paymentId, paymentMethod, receivedCents, saleId } = event.payload
    if (cashlogyRequestId || cashlogyTransactionId) {
      if (!cashlogyRequestId || !cashlogyTransactionId || paymentMethod !== 'cash' || receivedCents === null) {
        throw new Error('La identidad del cobro Cashlogy está incompleta.')
      }
    }
    const { error } = await supabase.rpc('change_sale_payment_method_safe', {
      p_event_id: event.id,
      p_tenant_id: event.tenantId,
      p_sale_id: saleId,
      p_payment_id: paymentId,
      p_payment_method: paymentMethod,
      p_received_cents: receivedCents,
      p_change_cents: changeCents,
      p_payload: event.payload,
      p_cashlogy_request_id: cashlogyRequestId ?? null,
      p_cashlogy_transaction_id: cashlogyTransactionId ?? null,
    })
    if (error) throw error

    return
  }

  if (event.kind === 'sale_voided') {
    const { error } = await supabase
      .from('tickets')
      .update({ status: 'void' })
      .eq('tenant_id', event.tenantId)
      .eq('id', event.payload.ticketId)
      .eq('status', 'paid')
    if (error) throw error
    return
  }

  const payload: CashClosedPayload = event.payload
  const { error } = await supabase
    .from('cash_sessions')
    .update({
      status: 'closed',
      closed_at: payload.closedAt,
      closed_by: payload.closedBy,
      expected_cash_cents: payload.expectedCashCents,
      expected_card_cents: payload.expectedCardCents,
      expected_invitation_cents: payload.expectedInvitationCents,
      expected_other_cents: payload.expectedOtherCents,
      counted_cash_cents: payload.countedCashCents,
      counted_card_cents: payload.countedCardCents,
      counted_invitation_cents: payload.countedInvitationCents,
      counted_other_cents: payload.countedOtherCents,
      discrepancy_cents: payload.discrepancyCents,
      notes: payload.notes,
    })
    .eq('id', payload.sessionId)
    .eq('tenant_id', payload.tenantId)
    .eq('status', 'open')

  if (error) {
    throw error
  }
}

export function subscribeToCashSessionChanges(
  context: TenantContext,
  onChange: () => void,
) {
  if (!supabase) {
    return () => undefined
  }

  const client = supabase
  const channel = client
    .channel(`cash-sessions-${context.tenantId}-${context.venueId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'cash_sessions',
        filter: `venue_id=eq.${context.venueId}`,
      },
      (payload) => {
        const session = (Object.keys(payload.new).length ? payload.new : payload.old) as {
          tenant_id?: string
        }

        if (session.tenant_id === context.tenantId) {
          onChange()
        }
      },
    )
    .subscribe()

  return () => {
    void client.removeChannel(channel)
  }
}
